import React from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { MessageContent } from './components/MessageContent'
import { MESSAGE_CONFIG } from './constants'
import type { MessageStatus } from './constants'
import { getMessageBridge } from './messageBridge'
import type { MessageStyles } from './messageBridge'
import type { MessageOptions } from './types'

const ICON_CLASS_MAP: Record<MessageStatus, keyof MessageStyles> = {
  info: 'iconInfo',
  success: 'iconSuccess',
  error: 'iconError',
  warning: 'iconWarning',
  loading: 'iconLoading'
}

let messageKey = 0

const resolveDuration = (
  status: MessageStatus,
  description: React.ReactNode,
  duration: number | undefined
) => {
  if (duration !== undefined) return duration
  if (status === 'error' || status === 'warning' || status === 'loading') {
    return 0
  }

  return description != null ? 6 : 4
}

function show(
  status: MessageStatus,
  content: React.ReactNode,
  options?: MessageOptions
) {
  const bridge = getMessageBridge()

  if (!bridge) {
    return
  }

  const key = options?.key ?? `design-system-message-${(messageKey += 1)}`
  const duration = resolveDuration(
    status,
    options?.description,
    options?.duration
  )
  const closable = options?.closable !== false
  const small = options?.size === 'small'
  const progress = status === 'loading' ? options?.progress : undefined
  const config = MESSAGE_CONFIG[status]

  bridge.message.open({
    key,
    type: status,
    duration,
    onClose: options?.onClose,
    className: [bridge.styles.notice, small ? bridge.styles.small : '']
      .filter(Boolean)
      .join(' '),
    icon: (
      <FontAwesomeIcon
        icon={config.icon}
        className={bridge.styles[ICON_CLASS_MAP[status]]}
        aria-hidden
      />
    ),
    content: (
      <MessageContent
        styles={bridge.styles}
        title={content}
        description={options?.description}
        progress={progress}
        closable={closable}
        closeLabel={typeof content === 'string' ? content : 'Close'}
        small={small}
        role={status === 'error' || status === 'warning' ? 'alert' : 'status'}
        holdOnFocus={duration > 0}
        onClose={() => bridge.message.destroy(key)}
      />
    )
  })
}

export function useMessage() {
  return {
    showInfo: (content: React.ReactNode, options?: MessageOptions) =>
      show('info', content, options),
    showSuccess: (content: React.ReactNode, options?: MessageOptions) =>
      show('success', content, options),
    showError: (content: React.ReactNode, options?: MessageOptions) =>
      show('error', content, options),
    showWarning: (content: React.ReactNode, options?: MessageOptions) =>
      show('warning', content, options),
    showLoading: (content: React.ReactNode, options?: MessageOptions) =>
      show('loading', content, options)
  }
}
